import { useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import parseUserAgent from '../../utils/parseUserAgent'

const ReportRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  margin-top: 0.75rem;
`

const ReportLink = styled.a`
  color: #aaa;
  font-size: 0.78rem;
  cursor: pointer;
  text-decoration: none;
  &:hover {
    color: #555;
  }
`

const ReportSeparator = styled.span`
  color: #ddd;
  font-size: 0.78rem;
`

const CopyButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: #aaa;
  font-size: 0.78rem;
  cursor: pointer;
  &:hover {
    color: #555;
  }
`

const HintText = styled.p`
  font-size: 0.78rem;
  color: #bbb;
  margin: 0.75rem 0 0.25rem;
`

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
  width: 100%;
  padding: 2rem;
`

const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 2.5rem 2rem;
  max-width: 420px;
  width: 100%;
  text-align: center;
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #fde8e8;
`

const IconCircle = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: #fff0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.25rem;
`

const Title = styled.h2`
  font-size: 1.1rem;
  font-weight: 700;
  color: #c62828;
  margin-bottom: 0.5rem;
`

const Message = styled.p`
  font-size: 0.85rem;
  color: #777;
  line-height: 1.6;
  margin-bottom: 1.75rem;
`

const PrimaryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  width: 100%;
  padding: 0.7rem;
  background-color: #d32f2f;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 0.6rem;
  transition: background-color 0.15s;
  &:hover {
    background-color: #b71c1c;
  }
`

const SecondaryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  width: 100%;
  padding: 0.7rem;
  background-color: #fff;
  color: #444;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 0;
  transition: background-color 0.15s;
  &:hover {
    background-color: #f9f9f9;
  }
`


const titleTranslations = {
  'Orders failed to load': 'Bestellungen konnten nicht geladen werden',
  'Products failed to load': 'Produkte konnten nicht geladen werden',
  'Shopping cart failed to load': 'Warenkorb konnte nicht geladen werden',
  "Cart summary couldn't load. Your cart items are safe.": 'Warenkorb konnte nicht geladen werden. Ihre Artikel sind sicher.',
  'Checkout failed to load': 'Kasse konnte nicht geladen werden',
  'Order confirmation failed to load': 'Auftragsbestätigung konnte nicht geladen werden',
  'Employee list failed to load': 'Mitarbeiterliste konnte nicht geladen werden',
  'Add employee failed to load': 'Mitarbeiter hinzufügen fehlgeschlagen',
  'Assets failed to load': 'Objekte konnten nicht geladen werden',
  'Ad hoc inventory failed to load': 'Ad-hoc-Inventur konnte nicht geladen werden',
  'Goods receipts failed to load': 'Wareneingang konnte nicht geladen werden',
  'Returns processing failed to load': 'Rückgabeverarbeitung konnte nicht geladen werden',
  'Inventory failed to load': 'Inventar konnte nicht geladen werden',
  'Inventory actions failed to load': 'Inventaraktionen konnten nicht geladen werden',
  'Appointments administration failed to load': 'Terminverwaltung konnte nicht geladen werden',
  'Office administration failed to load': 'Standortverwaltung konnte nicht geladen werden',
  'Create office failed to load': 'Standort erstellen konnte nicht geladen werden',
  'Edit office failed to load': 'Standort bearbeiten konnte nicht geladen werden',
  'Copy office failed to load': 'Standort kopieren konnte nicht geladen werden',
  'Office access rights failed to load': 'Standort Zugriffsrechte konnten nicht geladen werden',
  'Cost center administration failed to load': 'Kostenstellenverwaltung konnte nicht geladen werden',
  'Create cost center failed to load': 'Kostenstelle erstellen konnte nicht geladen werden',
  'Edit cost center failed to load': 'Kostenstelle bearbeiten konnte nicht geladen werden',
  'Copy cost center failed to load': 'Kostenstelle kopieren konnte nicht geladen werden',
  'Cost center access rights failed to load': 'Kostenstelle Zugriffsrechte konnten nicht geladen werden',
  'Cost center interfaces failed to load': 'Kostenstelle Schnittstellen konnten nicht geladen werden',
  'News administration failed to load': 'News-Verwaltung konnte nicht geladen werden',
  'Activities administration failed to load': 'Aktivitätenverwaltung konnte nicht geladen werden',
  'Create activity failed to load': 'Aktivität erstellen konnte nicht geladen werden',
  'Edit activity failed to load': 'Aktivität bearbeiten konnte nicht geladen werden',
  'Contingent templates failed to load': 'Kontingentvorlagen konnten nicht geladen werden',
  'Create contingent template failed to load': 'Kontingentvorlage erstellen konnte nicht geladen werden',
  'Edit contingent template failed to load': 'Kontingentvorlage bearbeiten konnte nicht geladen werden',
  'Placeholders administration failed to load': 'Textplatzhalter konnten nicht geladen werden',
  'Appointment types failed to load': 'Terminarten konnten nicht geladen werden',
  'Create appointment type failed to load': 'Terminart erstellen konnte nicht geladen werden',
  'Edit appointment type failed to load': 'Terminart bearbeiten konnte nicht geladen werden',
  'Cabin types failed to load': 'Kabinen konnten nicht geladen werden',
  'Create cabin type failed to load': 'Kabine erstellen konnte nicht geladen werden',
  'Edit cabin type failed to load': 'Kabine bearbeiten konnte nicht geladen werden',
  'Articles administration failed to load': 'Artikeladministration konnte nicht geladen werden',
  'Edit article failed to load': 'Artikel bearbeiten konnte nicht geladen werden',
  'Copy article failed to load': 'Artikel kopieren konnte nicht geladen werden',
  'Asset types administration failed to load': 'Typen verwalten konnte nicht geladen werden',
  'Create asset type failed to load': 'Typ erstellen konnte nicht geladen werden',
  'Edit asset type failed to load': 'Typ bearbeiten konnte nicht geladen werden',
  'Asset groups administration failed to load': 'Gruppen verwalten konnte nicht geladen werden',
  'Create asset group failed to load': 'Gruppe erstellen konnte nicht geladen werden',
  'Edit asset group failed to load': 'Gruppe bearbeiten konnte nicht geladen werden',
}

const InlineErrorFallback = ({ resetErrorBoundary, title }) => {
  const navigate = useNavigate()
  const [copied, setCopied] = useState(false)

  const isGerman = navigator.language.startsWith('de') || window.location.pathname.includes('/de/')
  const displayTitle = isGerman ? (titleTranslations[title] || title) : (title || 'Something went wrong')

  const lang = isGerman ? {
    subject: `Fehlerbericht – ${titleTranslations[title] || title || 'Bereichsfehler'}`,
    to: 'An: support@rigilog.com',
    section: 'Bereich',
    page: 'Seite',
    time: 'Zeit',
    prompt: 'Bitte beschreiben Sie, was Sie getan haben, als der Fehler aufgetreten ist:',
    message: 'In diesem Bereich ist ein Fehler aufgetreten. Nur dieser Abschnitt konnte nicht geladen werden. Der Rest der App funktioniert weiterhin normal.',
    tryAgain: 'Erneut versuchen',
    goToHome: 'Zur Startseite',
    openEmail: 'Per E-Mail melden',
    copyReport: 'Bericht kopieren',
    copied: 'Kopiert!',
    hint: 'Problem melden für eine schnellere Lösung:',
  } : {
    subject: `Bug Report – ${title || 'Section Error'}`,
    to: 'To: support@rigilog.com',
    section: 'Section',
    page: 'Page',
    time: 'Time',
    prompt: 'Please describe what you were doing when the error occurred:',
    message: 'Something went wrong in this section. Only this panel failed to render. The rest of the app is unaffected.',
    tryAgain: 'Try again',
    goToHome: 'Go to Home',
    openEmail: 'Report via email',
    copyReport: 'Copy report info',
    copied: 'Copied!',
    hint: 'Report this issue for a faster resolution:',
  }

  const reportText = `${lang.to}\nSubject: ${lang.subject}\n\n${lang.section}: ${displayTitle}\n${lang.page}: ${window.location.href}\n${lang.time}: ${new Date().toLocaleString()}\nBrowser: ${parseUserAgent()}\n\n${lang.prompt}\n`
  const mailtoUrl = `mailto:support@rigilog.com?subject=${encodeURIComponent(lang.subject)}&body=${encodeURIComponent(reportText)}`

  const handleCopy = () => {
    navigator.clipboard.writeText(reportText).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <Wrapper>
      <Card>
        <IconCircle>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#d32f2f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </IconCircle>

        <Title>{displayTitle}</Title>
        <Message>{lang.message}</Message>

        <PrimaryButton onClick={resetErrorBoundary}>
          ↺ {lang.tryAgain}
        </PrimaryButton>
        <SecondaryButton onClick={() => navigate('/')}>
          ⌂ {lang.goToHome}
        </SecondaryButton>

        <HintText>{lang.hint}</HintText>
        <ReportRow>
          <ReportLink href={mailtoUrl}>⚑ {lang.openEmail}</ReportLink>
          <ReportSeparator>|</ReportSeparator>
          <CopyButton onClick={handleCopy}>
            {copied ? `✓ ${lang.copied}` : `📋 ${lang.copyReport}`}
          </CopyButton>
        </ReportRow>
      </Card>
    </Wrapper>
  )
}

export default InlineErrorFallback
